import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useTeam } from '../../../context/TeamContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { getMascotByXP } from '../../../data/nurseAppModels';
import NurseBottomNavigation from '../Layout/NurseBottomNavigation';
import ThemeSelector from '../Theme/ThemeSelector';
import GiftDisplay from '../Gift/GiftDisplay';

const NurseProfilePage = () => {
  const { currentUser } = useAuth();
  const { teams } = useTeam();
  const [userData, setUserData] = useState(null);
  const [mascot, setMascot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showThemeSelector, setShowThemeSelector] = useState(false);

  useEffect(() => {
    if (currentUser) {
      fetchUserData();
    } else {
      setLoading(false);
    }
  }, [currentUser]);

  const fetchUserData = async () => {
    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const data = userSnap.data();
        setUserData(data);
        
        const xp = data.currentXP || 0;
        const mascotData = getMascotByXP(xp);
        setMascot(mascotData);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="container mx-auto px-4 py-6">
        {/* Profile Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center gap-4">
            {currentUser?.photoURL ? (
              <img 
                src={currentUser.photoURL} 
                alt="Profile" 
                className="w-20 h-20 rounded-full border-4 border-blue-500"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-blue-500 flex items-center justify-center text-white text-3xl font-semibold border-4 border-blue-500">
                {currentUser?.displayName?.charAt(0) || 'U'}
              </div>
            )}
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-800">
                {currentUser?.displayName || 'ผู้ใช้'}
              </h2>
              <p className="text-gray-600">{currentUser?.email}</p>
              {userData?.role && (
                <p className="text-sm text-gray-500 mt-1">
                  {userData.role === 'NURSE' ? 'พยาบาล' :
                   userData.role === 'ASSISTANT_NURSE' ? 'ผู้ช่วยพยาบาล' :
                   userData.role === 'DOCTOR' ? 'หมอ' :
                   userData.role === 'STAFF' ? 'เจ้าหน้าที่' : userData.role}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Mascot Level */}
        {mascot && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Level & Progress</h3>
            <div className="text-center">
              <div className="text-6xl mb-4">👼</div>
              <h4 className="text-xl font-bold text-gray-800 mb-2">
                Level {mascot.level}: {mascot.nameThai}
              </h4>
              <div className="mt-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>XP: {mascot.xp}</span>
                  {mascot.xpToNext && (
                    <span>ถึง Level {mascot.level + 1}: {mascot.xpToNext} XP</span>
                  )}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${mascot.progressPercent}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Teams */}
        {teams.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-lg font-bold text-gray-800 mb-4">ทีมของฉัน</h3>
            <div className="space-y-3">
              {teams.map(team => (
                <Link
                  key={team.id}
                  to={`/nurse/teams/${team.id}`}
                  className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                >
                  <p className="font-semibold text-gray-800">{team.name}</p>
                  <p className="text-sm text-gray-600">
                    {team.userRole === 'LEADER' ? 'หัวหน้าทีม' :
                     team.userRole === 'DEPUTY_LEADER' ? 'รองหัวหน้า' : 'สมาชิก'}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Gifts Received */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <GiftDisplay userId={currentUser.uid} />
        </div>

        {/* Settings */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">ตั้งค่า</h3>
          <div className="space-y-2">
            <button
              onClick={() => setShowThemeSelector(true)}
              className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
            >
              <div className="flex items-center justify-between">
                <span>เลือกธีม</span>
                <span className="text-sm text-gray-500">→</span>
              </div>
            </button>
            <Link
              to="/nurse/gifts/history"
              className="block p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
            >
              <div className="flex items-center justify-between">
                <span>ประวัติของขวัญ</span>
                <span className="text-sm text-gray-500">→</span>
              </div>
            </Link>
            <Link
              to="/nurse/profile/edit"
              className="block p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
            >
              แก้ไขโปรไฟล์
            </Link>
            <Link
              to="/nurse/profile/settings"
              className="block p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
            >
              ตั้งค่าระบบ
            </Link>
          </div>
        </div>
      </div>

      {/* Theme Selector Modal */}
      {showThemeSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">เลือกธีม</h2>
              <button
                onClick={() => setShowThemeSelector(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <ThemeSelector onClose={() => setShowThemeSelector(false)} />
          </div>
        </div>
      )}

      <NurseBottomNavigation />
    </div>
  );
};

export default NurseProfilePage;

